// messageCenter/noticeOverlay.js — important admin messages (display mode 'overlay') as a modal dialog on every backend
// page (bigplan backend/message-center, step 8c; D-21, D-36): title, text, one button "Verstanden". No X, and Esc does
// not close it — only the button counts as clicked away. The button acknowledges the notice (read, acknowledged, login
// memory; NoticeApi notice_acknowledge), then the next pending overlay follows; the counter on the inbox button follows
// (inboxEvents.js).
//
// Fed with the pending notices of each summary (inboxButton onSummary): the same notice arrives again until it is
// acknowledged, so a showing already queued or shown is not queued twice — but "show again" (a new reshown_at) is a new
// showing and comes at once, also on the page where it was clicked away before (smoke 8e). The text is a topoEditor value rendered
// headless — escaped text plus the entities' own markup — so innerHTML is safe.
import '../../../stylesheet_vite/src/modules/notice-overlay.css';
import { str } from '../i18n/str.js';
import { createDisposers } from '../core/disposers.js';
import { renderFrontendHtmlFromValue } from '../topoEditor/topoDoc.js';
import { noticeTextEntities } from './noticeText.js';
import { decorateDocLinks, openDoc } from './docLinks.js';
import { announceInboxChange, showingKey } from './inboxEvents.js';

export function createNoticeOverlay({ api, doc = document, parent = doc.body }) {
    const disposers = createDisposers();
    const entities = noticeTextEntities();
    const queue = [];
    const known = new Set();   // showings queued or shown on this page (showingKey: a "show again" is a new showing)
    let current = null;

    const dialog = doc.createElement('dialog');
    dialog.className = 'noticeOverlay';
    const title = doc.createElement('h2');
    title.className = 'noticeOverlay__title';
    const text = doc.createElement('div');
    text.className = 'noticeOverlay__text';
    const message = doc.createElement('p');
    message.className = 'noticeOverlay__message';
    message.hidden = true;
    const button = doc.createElement('button');
    button.type = 'button';
    button.className = 'topo2-btn topo2-btn--small topo2-btn--solid noticeOverlay__ok';
    button.textContent = str('MC_NOTICE_UNDERSTOOD');
    dialog.append(title, text, message, button);
    parent.append(dialog);
    disposers.add(() => dialog.remove());

    listen(dialog, 'cancel', (event) => event.preventDefault());   // D-36: Esc does not close it
    listen(button, 'click', () => acknowledge());
    listen(text, 'click', (event) => {
        const link = event.target.closest('.topo2-doclink');
        if (link) {
            event.preventDefault();
            openDoc(link);
        }
    });

    function listen(target, event, fn) {
        target.addEventListener(event, fn);
        disposers.add(() => target.removeEventListener(event, fn));
    }

    /** Takes the pending notices of a summary; queues the overlays not seen yet and shows the first one. */
    function show(pending = []) {
        for (const notice of pending) {
            if (notice.display !== 'overlay' || known.has(showingKey(notice))) { continue; }
            known.add(showingKey(notice));
            queue.push(notice);
        }
        if (!current) { next(); }
    }

    function next() {
        current = queue.shift() ?? null;
        if (!current) {
            close();
            return;
        }
        title.textContent = current.title;
        text.innerHTML = renderFrontendHtmlFromValue(current.body ?? '', entities);
        message.hidden = true;
        button.disabled = false;
        if (!dialog.open) {
            if (typeof dialog.showModal === 'function') { dialog.showModal(); } else { dialog.setAttribute('open', ''); }
        }
        decorateDocLinks(text);
        button.focus();
    }

    /** "Verstanden": only an acknowledged notice goes away; a failure says so and lets the person try again. */
    async function acknowledge() {
        if (!current) { return; }
        button.disabled = true;
        try {
            const response = await api.acknowledgeNotice(current.thread_id);
            if (!response?.success) { throw new Error(response?.error ?? 'no answer'); }
            announceInboxChange(doc);
            next();
        } catch (e) {
            console.error('message center: acknowledging the notice failed', e);
            message.textContent = str('MC_SAVE_FAILED');
            message.hidden = false;
            button.disabled = false;
        }
    }

    function close() {
        if (typeof dialog.close === 'function' && dialog.open) { dialog.close(); } else { dialog.removeAttribute('open'); }
    }

    return {
        show,
        isOpen: () => dialog.hasAttribute('open'),
        current: () => current,
        destroy: () => disposers.dispose(),
    };
}
